import React from "react";
import styled from "styled-components";
import { apiPost } from "../../api";
import { LoggedInUserContext } from "../../providers/ProvideLoggedInUser";

// Always-visible inline like / dislike / bookmark buttons for the
// rebranded `/sertifikasi/.../<lesson-slug>` page. Mirrors the icon set,
// colour palette, and optimistic-mutate semantics from
// `app/frontend/homes/CardActionsOverlay.jsx`, but renders inline next
// to the lesson title instead of as a hover overlay over a card image.
//
// Server props consumed:
//   resourceId          — Lesson UUID (drives /user_likes/create_v2 + /user_bookmarks/create_v2)
//   initialLiked        — bool, hydrated from LessonDetailPresenter.is_liked
//   initialDisliked     — bool, hydrated from LessonDetailPresenter.is_disliked
//   initialBookmarked   — bool, hydrated from LessonDetailPresenter.is_bookmarked
//
// Anonymous users get bounced to /login?redirect_after_login=<current> on
// any click (same pattern CardActionsOverlay uses for parity).

function redirectToLogin() {
  if (typeof window === "undefined") return;
  const back = encodeURIComponent(
    window.location.pathname + window.location.search + window.location.hash,
  );
  window.location.href = `/login?redirect_after_login=${back}`;
}

function isOkStatus(code) {
  return code >= 200 && code < 300;
}

function LessonInlineActions({
  resourceId,
  initialLiked = false,
  initialDisliked = false,
  initialBookmarked = false,
}) {
  const { signedIn, loading } = React.use(LoggedInUserContext);

  const [liked, setLiked] = React.useState(initialLiked);
  const [disliked, setDisliked] = React.useState(initialDisliked);
  const [bookmarked, setBookmarked] = React.useState(initialBookmarked);
  const [busy, setBusy] = React.useState(false);

  const toggleLike = React.useCallback(
    async (likesValue) => {
      if (loading) return;
      if (!signedIn) return redirectToLogin();
      if (busy) return;
      setBusy(true);
      const prevLiked = liked;
      const prevDisliked = disliked;
      if (likesValue) {
        setLiked(!prevLiked);
        if (!prevLiked) setDisliked(false);
      } else {
        setDisliked(!prevDisliked);
        if (!prevDisliked) setLiked(false);
      }
      try {
        const { statusCode } = await apiPost("/user_likes/create_v2", {
          body: {
            user_likes: {
              resource_type: "Lesson",
              resource_id: resourceId,
              likes: likesValue,
            },
          },
        });
        if (!isOkStatus(statusCode)) {
          setLiked(prevLiked);
          setDisliked(prevDisliked);
        }
      } catch (err) {
        if (typeof console !== "undefined")
          console.warn("[LessonInlineActions] like failed", err);
        setLiked(prevLiked);
        setDisliked(prevDisliked);
      } finally {
        setBusy(false);
      }
    },
    [loading, signedIn, busy, liked, disliked, resourceId],
  );

  const toggleBookmark = React.useCallback(async () => {
    if (loading) return;
    if (!signedIn) return redirectToLogin();
    if (busy) return;
    setBusy(true);
    const prev = bookmarked;
    setBookmarked(!prev);
    try {
      const { statusCode } = await apiPost("/user_bookmarks/create_v2", {
        body: {
          user_bookmarks: {
            resource_type: "Lesson",
            resource_id: resourceId,
          },
        },
      });
      if (!isOkStatus(statusCode)) setBookmarked(prev);
    } catch (err) {
      if (typeof console !== "undefined")
        console.warn("[LessonInlineActions] bookmark failed", err);
      setBookmarked(prev);
    } finally {
      setBusy(false);
    }
  }, [loading, signedIn, busy, bookmarked, resourceId]);

  if (!resourceId) return null;

  return (
    <Row role="group" aria-label="Aksi materi">
      <IconButton
        type="button"
        aria-label={liked ? "Batalkan suka" : "Suka"}
        aria-pressed={liked}
        disabled={busy}
        $active={liked}
        $activeColor="#1BEB4B"
        onClick={() => toggleLike(true)}
      >
        <i
          className={`${liked ? "fas" : "far"} fa-thumbs-up`}
          aria-hidden="true"
        />
      </IconButton>
      <IconButton
        type="button"
        aria-label={disliked ? "Batalkan tidak suka" : "Tidak suka"}
        aria-pressed={disliked}
        disabled={busy}
        $active={disliked}
        $activeColor="#FF3447"
        onClick={() => toggleLike(false)}
      >
        <i
          className={`${disliked ? "fas" : "far"} fa-thumbs-down`}
          aria-hidden="true"
        />
      </IconButton>
      <IconButton
        type="button"
        aria-label={bookmarked ? "Hapus bookmark" : "Bookmark"}
        aria-pressed={bookmarked}
        disabled={busy}
        $active={bookmarked}
        $activeColor="#d85907"
        onClick={toggleBookmark}
      >
        <i
          className={`${bookmarked ? "fas" : "far"} fa-bookmark`}
          aria-hidden="true"
        />
      </IconButton>
    </Row>
  );
}

export default React.memo(LessonInlineActions);

const Row = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const IconButton = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  color: #1f2937;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition:
    transform 140ms ease,
    background 140ms ease,
    color 140ms ease,
    box-shadow 140ms ease,
    border-color 140ms ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);

  & i {
    font-size: 16px;
    line-height: 1;
    display: block;
  }

  &:hover,
  &:focus-visible {
    transform: translateY(-2px);
    background: #fff;
    color: #d85907;
    border-color: #fdba74;
    box-shadow: 0 6px 14px rgba(0, 0, 0, 0.12);
  }

  &:focus-visible {
    outline: 2px solid #d85907;
    outline-offset: 2px;
  }

  ${({ $active, $activeColor }) =>
    $active &&
    `
      color: ${$activeColor || "#d85907"};
      border-color: ${$activeColor || "#d85907"};

      &:hover, &:focus-visible {
        color: ${$activeColor || "#d85907"};
        border-color: ${$activeColor || "#d85907"};
        background: #ffffff;
      }
    `}

  &:disabled {
    opacity: 0.7;
    cursor: wait;
  }

  [data-theme="dark"] & {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.12);
    color: #f3f4f6;
  }
`;
